import React, {
  useCallback, useEffect, useMemo, useState, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../../components/PageTitle';
import useInputState from '../../helpers/hooks/useInputState';
import { getEventsRequest, cancelEventsRequest } from '../../store/actions/events';
import EventItem from './components/EventItem';
import EventModal from './components/EventModal';
import {
  EVENT_CATEGORY_NONE,
  EVENT_FILTERS,
  EVENT_MODAL_STATES,
  EVENT_SOURCES,
} from './constants';
import MyButton from '../../components/MyButton';
import AddOrgIcon from '../../assets/formIcons/addOrg.svg';
import MyTable from '../../components/MyTable';
import BulkEditSelectedModal from '../../components/BulkEditSelectedModal';
import './css/index.scss';
import Api from '../../Api';
import FiltersPanel, {
  FilterCheckbox,
  FilterDateRange,
  FilterMultiSelect,
  FilterSelect,
  FiltersGroup,
} from '../../components/SearchFilters';
import useCitiesOfCountry from '../../helpers/hooks/useCitiesOfCountry';
import { getCountriesRequest, getLanguagesRequest } from '../../store/actions/staticData';
import {
  EVENT_SORT_OPTIONS,
  buildEventSearchParams,
  describeEventFilters,
  formatSearchInfo,
  getDefaultEventFilters,
  hasDateFiltersError,
  validateDateFilters,
} from '../../helpers/searchFilters';

const EVENT_SOURCE_OPTIONS = EVENT_SOURCES.map((source) => ({ value: source.id, label: source.name }));

function Events() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    events,
    eventsTotal,
    eventsAllTotal,
    eventsActiveTotal,
    eventsBlockedTotal,
    eventsSearchInfo,
    eventsLoading,
    eventsErrorMessage,
  } = useSelector((store) => store.events);
  const { countries, languages } = useSelector((store) => store.staticData);

  const [page, setPage] = useInputState(1);
  const [perPage, setPerPage] = useState(100);
  const [selectedFilter, setSelectedFilter] = useState(EVENT_FILTERS[0]);
  const [q, setQ] = useInputState();
  const [isInit, setIsInit] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalState, setModalState] = useState('');
  const [selectedEvent, setSelectedEvent] = useState({});
  const [itemsPassed, setItemsPassed] = useState(0);
  const abortControllerRef = useRef(null);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [filters, setFilters] = useState(getDefaultEventFilters);
  const { cityOptions, citiesLoading } = useCitiesOfCountry(filters.countryId);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);

  const getEvents = ({ paramsPage, paramsPerPage = perPage, isInitRequired = true }) => {
    if (isInitRequired && !isInit) return;
    if (hasDateFiltersError(filters)) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const params = buildEventSearchParams({
      q,
      page: paramsPage,
      perPage: paramsPerPage,
      isActive: selectedFilter.id,
      filters,
    });

    dispatch(getEventsRequest(params, abortController));
    setSelectedEvents([]);
  };

  const handleSearch = () => {
    setPage(1);
    getEvents({ paramsPage: 1 });
  };

  const handleCancelSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    dispatch(cancelEventsRequest());
  };

  const updateFilters = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const resetFilters = () => setFilters((prev) => ({ ...getDefaultEventFilters(), sort: prev.sort }));

  const countryOptions = useMemo(() => (countries || [])
    .filter((country) => !country.is_online)
    .map((country) => ({ value: country._id, label: country.name })), [countries]);

  const languageOptions = useMemo(
    () => (languages || []).map((language) => ({ value: language._id, label: language.name })),
    [languages],
  );

  const eventCategoryOptions = useMemo(
    () => categoryOptions.map((category) => ({ value: category.id, label: category.name })),
    [categoryOptions],
  );

  const dateErrors = validateDateFilters(filters);

  const filterChips = useMemo(() => describeEventFilters(filters, {
    countries: countryOptions,
    cities: cityOptions,
    languages: languageOptions,
    eventCategories: eventCategoryOptions,
    sources: EVENT_SOURCE_OPTIONS,
  }), [filters, countryOptions, cityOptions, languageOptions, eventCategoryOptions]);

  const extraFilters = (
    <FiltersPanel
      chips={filterChips}
      onRemoveChip={(chip) => updateFilters(chip.reset)}
      onReset={resetFilters}
    >
      <FiltersGroup title="Где">
        <FilterSelect
          label="Страна"
          value={filters.countryId}
          options={countryOptions}
          placeholder="Все страны"
          onChange={(countryId) => updateFilters({ countryId, cityIds: [] })}
        />
        <FilterMultiSelect
          label="Города"
          options={cityOptions}
          values={filters.cityIds}
          onChange={(cityIds) => updateFilters({ cityIds })}
          placeholder="Все города"
          emptyText="В стране нет городов"
          loading={citiesLoading}
          disabled={!filters.countryId}
          hint={!filters.countryId ? 'Сначала выберите страну' : undefined}
        />
      </FiltersGroup>

      <FiltersGroup title="Мероприятие">
        <FilterMultiSelect
          label="Категории"
          options={eventCategoryOptions}
          values={filters.categoryIds}
          onChange={(categoryIds) => updateFilters({ categoryIds })}
          placeholder="Все категории"
          emptyText="Категории не загружены"
        />
        <FilterDateRange
          label="Даты проведения"
          from={filters.dateFrom}
          to={filters.dateTo}
          error={dateErrors.date}
          onChange={({ from, to }) => updateFilters({ dateFrom: from, dateTo: to })}
        />
        <FilterMultiSelect
          label="Языки"
          options={languageOptions}
          values={filters.languageIds}
          onChange={(languageIds) => updateFilters({ languageIds })}
          placeholder="Все языки"
          emptyText="Языки не загружены"
          hint="Плюс карточки «для всех языков»"
        />
        <FilterCheckbox
          label="Показывать прошедшие"
          value={filters.includeExpired}
          onChange={(includeExpired) => updateFilters({ includeExpired })}
        />
      </FiltersGroup>

      <FiltersGroup title="Служебные">
        <FilterMultiSelect
          label="Источник"
          options={EVENT_SOURCE_OPTIONS}
          values={filters.sources}
          onChange={(sources) => updateFilters({ sources })}
          placeholder="Все источники"
        />
        <FilterDateRange
          label="Дата создания"
          from={filters.createdFrom}
          to={filters.createdTo}
          error={dateErrors.created}
          onChange={({ from, to }) => updateFilters({ createdFrom: from, createdTo: to })}
        />
        <FilterSelect
          label="Сортировка"
          value={filters.sort}
          options={EVENT_SORT_OPTIONS}
          allowEmpty={false}
          onChange={(sort) => updateFilters({ sort })}
        />
      </FiltersGroup>
    </FiltersPanel>
  );

  const TableItemComponent = useCallback((item, index) => (
    <EventItem
      key={item._id}
      item={item}
      itemsPassed={itemsPassed}
      index={index}
      setSelectedEvent={setSelectedEvent}
      setModalState={setModalState}
      setIsModalVisible={setIsModalVisible}
      withCheckbox={selectedEvents.length > 0}
      isSelected={selectedEvents.includes(item._id)}
      setSelected={setSelectedEvents}
    />
  ), [itemsPassed, selectedEvents]);

  const WhileSelectingComponents = useCallback(() => (
    selectedEvents?.length ? (
      <div className="table_selection_bar">
        <span className="table_selection_bar_count">
          Выбрано:
          {' '}
          {selectedEvents.length}
        </span>
        <div
          className="pressable table_selection_bar_action"
          onClick={() => setSelectedEvents(events.map((item) => item._id))}
        >
          Выбрать все на странице
        </div>
        <div
          className="pressable table_selection_bar_action"
          onClick={() => setSelectedEvents([])}
        >
          Снять выбор со всех
        </div>
        <div
          onClick={() => setIsBulkEditOpen(true)}
          className="pressable table_selection_bar_action"
        >
          Редактировать
        </div>
        <div
          onClick={() => {
            setModalState(EVENT_MODAL_STATES.block);
            setIsModalVisible(true);
          }}
          className="pressable table_selection_bar_action"
        >
          Забанить
        </div>
        <div
          onClick={() => {
            setModalState(EVENT_MODAL_STATES.fullyDelete);
            setIsModalVisible(true);
          }}
          className="pressable table_selection_bar_action"
        >
          Удалить полностью
        </div>
      </div>
    ) : null
  ), [selectedEvents, events]);

  const handlePage = (newVal) => {
    if (newVal === page) return;
    setPage(newVal);
    getEvents({ paramsPage: newVal });
  };

  const onCreateEventClick = () => {
    navigate('/events/create');
  };

  useEffect(() => {
    dispatch(getCountriesRequest());
    dispatch(getLanguagesRequest());
  }, []);

  useEffect(() => {
    Api.getEventCategories()
      .then(({ data }) => {
        const categories = (data?.categories || [])
          .slice()
          .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
          .map((category) => ({ id: category._id, name: category.name }));

        setCategoryOptions([
          ...categories,
          { id: EVENT_CATEGORY_NONE, name: 'Без категории' },
        ]);
      })
      .catch((error) => {
        console.warn(error, 'while loading event categories');
      });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      getEvents({ paramsPage: 1, paramsPerPage: perPage < 1 ? 1 : perPage > 500 ? 500 : perPage });
      if (perPage < 1) setPerPage(1);
      else if (perPage > 500) setPerPage(500);
    }, 600);

    return () => clearTimeout(timer);
  }, [perPage]);

  useEffect(() => {
    setPage(1);
    getEvents({ paramsPage: 1 });
  }, [selectedFilter]);

  // Фильтры применяются сразу (с небольшой задержкой, чтобы не слать запрос на каждый клик).
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    if (!isInit) return undefined;
    const timer = setTimeout(() => {
      setPage(1);
      getEvents({ paramsPage: 1 });
    }, 300);
    return () => clearTimeout(timer);
  }, [filtersKey]);

  useEffect(() => {
    getEvents({ paramsPage: page, isInitRequired: false });
    setTimeout(() => {
      setIsInit(true);
    }, 500);
  }, []);

  useEffect(() => {
    setItemsPassed((page - 1) * perPage);
  }, [events, page, perPage]);

  return (
    <div className="events_page">
      <PageTitle
        text="Мероприятия (во всех странах)"
        RightComponent={(
          <MyButton
            text="Добавить"
            svgPath={AddOrgIcon}
            className="org_add_btn"
            onClick={onCreateEventClick}
          />
        )}
      />

      <MyTable
        q={q}
        setQ={setQ}
        perPage={perPage}
        setPerPage={setPerPage}
        filters={EVENT_FILTERS}
        filtersCount={{
          [EVENT_FILTERS[0].id]: eventsAllTotal || 0,
          [EVENT_FILTERS[1].id]: eventsActiveTotal || 0,
          [EVENT_FILTERS[2].id]: eventsBlockedTotal || 0,
        }}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        extraFilters={extraFilters}
        Item={TableItemComponent}
        WhileSelectingComponents={WhileSelectingComponents}
        data={events}
        page={page}
        setPage={handlePage}
        total={eventsTotal}
        onSearch={handleSearch}
        isLoading={eventsLoading}
        onCancelSearch={handleCancelSearch}
        searchHint={formatSearchInfo(eventsSearchInfo)}
        errorText={eventsErrorMessage}
      />

      <EventModal
        selectedEvent={selectedEvent}
        modalState={modalState}
        isVisible={isModalVisible}
        setIsVisible={setIsModalVisible}
        setSelectedEvent={setSelectedEvent}
        selectedEvents={selectedEvents}
        setSelectedEvents={setSelectedEvents}
        getEvents={() => {
          setPage(1);
          getEvents({ paramsPage: 1, isInitRequired: false });
        }}
      />

      <BulkEditSelectedModal
        isOpen={isBulkEditOpen}
        onClose={() => setIsBulkEditOpen(false)}
        entityType="event"
        entityLabel="мероприятий"
        selectedItems={events.filter((item) => selectedEvents.includes(item._id))}
        onFinished={() => {
          setSelectedEvents([]);
          getEvents({ paramsPage: page, isInitRequired: false });
        }}
      />
    </div>
  );
}

export default Events;
